import { AlertTriangle, Building2, ChevronRight, Loader2, MapPin, RefreshCw, User } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { APPROX_HINT, formatRegDate } from '../utils/registrationDate';
import { LIFECYCLE_BADGE } from './cafeLifecycleBadge';
import { billingBadge } from '../utils/billingStatus';

/**
 * Single source of truth for how a café row renders — used by CafeRegistry.jsx
 * (the full registry) and Dashboard.jsx (top 5 by revenue). Both used to carry
 * their own copy of this table; they drifted (different avatar size, a second
 * lossy status-label derivation on the dashboard that showed "Payment Due" for
 * an expired/suspended café). One component now, so a future change to how a
 * café row looks only has one place to happen.
 *
 * 2026-10-16: one tidy line per café — location and owner moved under the
 * name (their columns made every row wrap), the PCs cell says what is wrong
 * when something is (missing / on an old version / uninstalled / gone quiet, from the
 * 30-minute sweep), and the licence badge became the billing state, which is
 * what actually matters (licence status did not reflect reality).
 */
export const CAFE_STATUS_CONFIG = {
  active: { label: 'Active', color: 'success' },
  trial: { label: 'Trial', color: 'info' },
  expiring: { label: 'Expiring', color: 'warning' },
  expired: { label: 'Expired', color: 'danger' },
  none: { label: 'No License', color: 'neutral' },
  suspended: { label: 'Suspended', color: 'danger' },
};

// Up to two letters from the café's name ("Ignite Premium…" -> "IP"), skipping
// "the" and symbols; one word gives its first two letters.
const initials = (name) => {
  const words = String(name || '').replace(/[^\p{L}\p{N}\s]/gu, ' ').split(/\s+/)
    .filter((w) => w && w.toLowerCase() !== 'the');
  if (words.length === 0) return '?';
  const pick = words.length === 1 ? words[0].slice(0, 2) : words[0][0] + words[1][0];
  return pick.toUpperCase();
};
// A steady colour per café (from its id), so a café looks the same every visit.
const AVATAR_HUES = [24, 200, 140, 280, 340, 50, 175, 230];
const avatarStyle = (cafe) => {
  const hue = AVATAR_HUES[Math.abs(Number(cafe.id) || 0) % AVATAR_HUES.length];
  return { background: `hsla(${hue}, 70%, 55%, 0.18)`, color: `hsl(${hue}, 80%, 72%)` };
};

const daysSince = (iso) => Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);

/** Under the online count, only when something is wrong at the café. */
function PcProblems({ cafe }) {
  if (cafe.quiet_since) {
    const days = daysSince(cafe.quiet_since);
    return (
      <span className="pcs-cell__problems" title="No PC at this café has connected since then">
        <AlertTriangle size={12} /> No PC for {days} day{days === 1 ? '' : 's'}
      </span>
    );
  }
  // Worst first, each in the café page's colour; the dot takes the worst one's.
  // "on old version", never a bare "stuck" (it read as a frozen PC).
  const parts = [
    cafe.pcs_uninstalled > 0 && ['is-uninstalled', `${cafe.pcs_uninstalled} uninstalled`],
    cafe.pcs_missing > 0 && ['is-missing', `${cafe.pcs_missing} missing`],
    cafe.pcs_stuck > 0 && ['is-old', `${cafe.pcs_stuck} on old version`],
  ].filter(Boolean);
  if (parts.length === 0) return null;
  return (
    <span className="pcs-cell__problems is-list">
      <span className={parts[0][0]}>●</span>
      {parts.flatMap(([cls, text], i) => [i ? ' · ' : ' ', <span key={cls} className={cls}>{text}</span>])}
    </span>
  );
}

const formatCurrency = (v) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(v);

/**
 * @param {object[]} cafes - rows shaped like /internal/cafes (CafeListItem):
 *   id, name, location, owner_name, owner_email, pc_count, online_pc_count,
 *   fee_starts_at, fee_active, pcs_missing, pcs_stuck, pcs_uninstalled,
 *   quiet_since, revenue.
 * @param {boolean} [showOwner=true] - leave the owner out of the line under the name.
 * @param {string} [revenueLabel='Revenue (MTD)']
 * @param {string} [emptyMessage='No cafés found']
 * @param {number} [maxHeight] - caps the table in a scrolling panel instead of
 *   letting a long list push the rest of the page down (Dashboard's usage).
 * @param {boolean} [showCreated=false] - add the "Created" column (the full registry).
 */
export default function CafeTable({
  cafes,
  isLoading = false,
  error = null,
  onRetry,
  showOwner = true,
  revenueLabel = 'Revenue (MTD)',
  emptyMessage = 'No cafés found',
  maxHeight,
  showCreated = false,
}) {
  const navigate = useNavigate();
  const colSpan = 5 + (showCreated ? 1 : 0);

  const table = (
    <table className="data-table cafe-table-wrap">
      <thead>
        <tr>
          <th>Café</th>
          <th>PCs</th>
          <th>Billing</th>
          <th>{revenueLabel}</th>
          {showCreated && <th>Created</th>}
          <th></th>
        </tr>
      </thead>
      <tbody>
        {isLoading ? (
          <tr>
            <td colSpan={colSpan}>
              <div className="empty-state">
                <Loader2 size={32} className="spin" />
                <p>Loading cafés...</p>
              </div>
            </td>
          </tr>
        ) : error ? (
          <tr>
            <td colSpan={colSpan}>
              <div className="empty-state empty-state--error">
                <p>{error}</p>
                {onRetry && (
                  <button className="btn btn--secondary btn--sm" onClick={onRetry}>
                    <RefreshCw size={14} /> Retry
                  </button>
                )}
              </div>
            </td>
          </tr>
        ) : cafes.length === 0 ? (
          <tr>
            <td colSpan={colSpan}>
              <div className="empty-state">
                <Building2 size={48} />
                <p>{emptyMessage}</p>
              </div>
            </td>
          </tr>
        ) : (
          cafes.map((cafe) => {
            // An archived/deleted café shows that, not its billing state.
            const status = LIFECYCLE_BADGE[cafe.status] || billingBadge(cafe);
            const owner = showOwner ? (cafe.owner_name || cafe.owner_email) : null;
            return (
              <tr key={cafe.id} className="clickable" onClick={() => navigate(`/cafes/${cafe.id}`)}>
                <td>
                  <div className="cafe-cell">
                    <div className="cafe-cell__avatar" style={avatarStyle(cafe)} aria-hidden="true">
                      {initials(cafe.name)}
                    </div>
                    <div className="cafe-cell__text">
                      <span className="cafe-cell__name">{cafe.name}</span>
                      {(cafe.location || owner) && (
                        <span className="cafe-cell__meta">
                          {cafe.location && <span><MapPin size={12} />{cafe.location}</span>}
                          {owner && <span><User size={12} />{owner}</span>}
                        </span>
                      )}
                    </div>
                  </div>
                </td>
                <td>
                  <div className="pcs-cell">
                    {cafe.pc_count > 0
                      ? <span><b>{cafe.online_pc_count || 0}/{cafe.pc_count}</b> online</span>
                      : <span className="pcs-cell__none">No PCs yet</span>}
                    <PcProblems cafe={cafe} />
                  </div>
                </td>
                <td>
                  <span className={`badge badge--${status.color}${status.soon ? ' cafe-cell__trial-soon' : ''}`}>{status.label}</span>
                </td>
                <td className="font-mono text-primary">
                  {cafe.revenue ? formatCurrency(cafe.revenue) : '—'}
                </td>
                {showCreated && (
                  <td
                    className={cafe.created_at && !cafe.created_at_approx ? 'text-secondary' : 'text-tertiary'}
                    title={cafe.created_at_approx ? APPROX_HINT : undefined}
                  >
                    {formatRegDate(cafe.created_at, cafe.created_at_approx, { short: true })}
                  </td>
                )}
                <td><ChevronRight size={16} className="text-tertiary" /></td>
              </tr>
            );
          })
        )}
      </tbody>
    </table>
  );

  return (
    <>
      {maxHeight ? <div style={{ maxHeight, overflowY: 'auto' }}>{table}</div> : table}
      <style>{`
        .cafe-cell { display: flex; align-items: center; gap: var(--space-3); }
        .cafe-cell__avatar {
          width: 36px; height: 36px;
          background: var(--accent-primary-subtle);
          border-radius: var(--radius-md);
          display: flex; align-items: center; justify-content: center;
          color: white;
        }
        .cafe-cell__avatar { flex-shrink: 0; font-size: var(--text-xs); font-weight: 700; letter-spacing: 0.02em; }
        .pcs-cell__none { color: var(--text-tertiary); }
        .cafe-cell__text { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
        .cafe-cell__name { font-weight: 500; }
        /* Location and owner, small and grey; wraps on a narrow screen rather
           than widening the table. */
        .cafe-cell__meta { display: flex; flex-wrap: wrap; gap: 2px var(--space-3); font-size: var(--text-xs); color: var(--text-tertiary); white-space: normal; }
        .cafe-cell__meta > span { display: inline-flex; align-items: center; gap: 4px; }
        .cafe-cell__trial-soon { box-shadow: inset 0 0 0 1px var(--status-warning); }
        .pcs-cell { display: flex; flex-direction: column; gap: 2px; color: var(--text-secondary); white-space: nowrap; }
        .pcs-cell b { color: var(--text-primary); font-weight: 600; }
        .pcs-cell__problems { font-size: var(--text-xs); font-weight: 600; color: var(--status-danger); display: inline-flex; align-items: center; gap: 4px; }
        .pcs-cell__problems.is-list { display: block; color: var(--text-tertiary); white-space: pre; }
        .pcs-cell__problems .is-uninstalled { color: #f04b4b; }
        .pcs-cell__problems .is-missing { color: #fb7a3c; }
        .pcs-cell__problems .is-old { color: #f5c024; }
        /* Badges and headings stay on one line; the café cell is the one that wraps. */
        .cafe-table-wrap .badge, .cafe-table-wrap th { white-space: nowrap; }
      `}</style>
    </>
  );
}
